import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { hostname } from "../../../utils";
import { BrowserIntegrationParameters } from "../../../types";

interface BrowserSessionIntegrationFormProps {
  editMode: boolean;
  initialData?: {
    url?: string;
    name?: string;
    parameters?: BrowserIntegrationParameters;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
  getToken: () => Promise<string>;
  companyId: string;
}

export default function BrowserSessionIntegrationForm({
  editMode,
  initialData = {},
  onValidationChange,
  onDataChange,
  getToken,
  companyId,
}: BrowserSessionIntegrationFormProps) {
  const [url, setUrl] = useState(initialData.url || "");
  const [useResidentialProxy, setUseResidentialProxy] = useState(false);
  const [zipCode, setZipCode] = useState("");

  // Login credentials state
  const [enableLoginCredentials, setEnableLoginCredentials] = useState(false);
  const [credentialDomain, setCredentialDomain] = useState("");
  const [credentialFields, setCredentialFields] = useState<Array<{ key: string; value: string }>>([
    { key: "username", value: "" },
    { key: "password", value: "" },
  ]);
  const [loginInstructions, setLoginInstructions] = useState("");

  // Browserbase session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<'idle' | 'creating' | 'ready' | 'saving' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState("");
  // Additional session data needed for saving (returned from create endpoint)
  const [sessionData, setSessionData] = useState<{
    contextId?: string;
    zipCode?: string;
    os?: string;
    fingerprint?: any;
    userAgent?: string;
    startUrl?: string;
  }>({});

  // Validation effect
  useEffect(() => {
    let isValid = false;

    if (editMode) {
      // In edit mode, form is always valid since we just show info
      isValid = true;
    } else {
      // In create mode, need session to be saved successfully
      isValid = sessionStatus === 'success';
    }

    onValidationChange(isValid);

    // Update parent with form data when successful
    if (sessionStatus === 'success' && sessionId) {
      // Build login credentials if enabled
      let loginCredentials = undefined;
      if (enableLoginCredentials) {
        const credentials: Record<string, string> = {};
        credentialFields.forEach((field) => {
          if (field.key && field.value) {
            credentials[field.key] = field.value;
          }
        });

        if (Object.keys(credentials).length > 0) {
          loginCredentials = [
            {
              domain: credentialDomain || undefined,
              credentials,
            },
          ];
        }
      }

      // Debug logging
      console.log("BrowserSessionIntegrationForm - onDataChange called with:");
      console.log("  loginCredentials:", loginCredentials);
      console.log("  loginInstructions:", loginInstructions || undefined);
      console.log("  sessionData:", sessionData);

      onDataChange({
        sessionId,
        url,
        loginCredentials,
        loginInstructions: loginInstructions || undefined,
        // Include session data for multi-instance compatibility
        ...sessionData,
      });
    }
  }, [sessionStatus, sessionId, url, editMode, enableLoginCredentials, credentialDomain, credentialFields, loginInstructions, sessionData, onValidationChange, onDataChange]);

  const createBrowserSession = async () => {
    try {
      setErrorMessage("");
      setSessionStatus('creating');

      const token = await getToken();
      const requestBody: any = {
        companyId,
        integrationType: 'browser_session',
        startUrl: url,
      };

      // Only include zipCode if using residential proxy
      if (useResidentialProxy && zipCode) {
        requestBody.zipCode = zipCode;
      }

      const response = await fetch(`${hostname}/browser-sessions/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create browser session');
      }

      const data = await response.json();
      setSessionId(data.sessionId);
      setLiveUrl(data.liveUrl);
      // Store all session data for saving later (multi-instance compatibility)
      setSessionData({
        contextId: data.contextId,
        zipCode: data.zipCode,
        os: data.os,
        fingerprint: data.fingerprint,
        userAgent: data.userAgent,
        startUrl: data.startUrl,
      });
      setSessionStatus('ready');

    } catch (error: any) {
      console.error('Failed to create browser session:', error);
      setErrorMessage(error.message || 'Failed to create browser session');
      setSessionStatus('error');
    }
  };

  const saveBrowserSession = async () => {
    if (!sessionId) {
      setErrorMessage('No active session');
      return;
    }

    if (!url.trim()) {
      setErrorMessage('Please enter a URL');
      return;
    }

    // Mark as success so parent dialog knows the session is ready to save
    // The actual save will happen when parent calls installBrowserSession
    setSessionStatus('success');
  };

  const cancelSession = async () => {
    // If we have an active session, tell the backend to shut it down
    if (sessionId) {
      try {
        const token = await getToken();
        await fetch(`${hostname}/browser-sessions/cancel/${sessionId}?companyId=${companyId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // Don't need to check response - just best effort cleanup
      } catch (error) {
        console.error('Failed to cancel browser session on backend:', error);
        // Continue with local cleanup even if backend call fails
      }
    }

    // Reset local state
    setSessionId(null);
    setLiveUrl(null);
    setSessionStatus('idle');
    setErrorMessage("");
  };

  // Edit mode - just show integration info
  if (editMode) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This browser session was saved via live browser session.
            To update the credentials, delete this integration and create a new one.
          </Typography>
        </Alert>

        <TextField
          fullWidth
          label="URL"
          value={url}
          disabled
          helperText="URL used for browser session"
          sx={{ mb: 2 }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Live Browser Session Login
        </Typography>
        <Typography variant="body2" gutterBottom>
          Enter the URL of the website you want to log into. We'll open a live browser
          session where you can log in yourself. Your credentials are never sent to our
          servers - you log in directly in the browser.
        </Typography>
        <Typography variant="body2">
          After logging in, click "Save Session" to complete the integration.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
          Cost: {useResidentialProxy ? '50' : '25'} credits per browser task
        </Typography>
      </Alert>

      {sessionStatus === 'idle' && (
        <>
          <TextField
            fullWidth
            type="url"
            label="Website URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            helperText="The URL of the website you want to log into (e.g., https://example.com/login)"
            sx={{ mb: 2 }}
            required
            placeholder="https://example.com/login"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={useResidentialProxy}
                onChange={(e) => setUseResidentialProxy(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Use Residential Proxy (recommended for social media)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Good for social media and sites that block browser automation. Costs 50 credits vs 25 credits without proxy.
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          {useResidentialProxy && (
            <TextField
              fullWidth
              label="ZIP Code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              helperText="Used for location-based proxy configuration"
              sx={{ mb: 2 }}
              required
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={enableLoginCredentials}
                onChange={(e) => setEnableLoginCredentials(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Add Login Credentials (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Credentials are securely stored and never visible to the AI model.
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          {enableLoginCredentials && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Login Credentials
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  Only needed for sessions that expire quickly. Sites with long-lived sessions can be logged into directly in the browser without providing credentials.
                </Typography>
              </Alert>

              <TextField
                fullWidth
                label="Domain Pattern (Optional)"
                value={credentialDomain}
                onChange={(e) => setCredentialDomain(e.target.value)}
                helperText="Leave empty for all domains, or specify a pattern like 'https://*.example.com'"
                sx={{ mb: 2 }}
                placeholder="https://*.example.com"
              />

              {credentialFields.map((field, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Field Name"
                    value={field.key}
                    onChange={(e) => {
                      const newFields = [...credentialFields];
                      newFields[index].key = e.target.value;
                      setCredentialFields(newFields);
                    }}
                    placeholder="username, email, etc."
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Field Value"
                    value={field.value}
                    onChange={(e) => {
                      const newFields = [...credentialFields];
                      newFields[index].value = e.target.value;
                      setCredentialFields(newFields);
                    }}
                    type={field.key.toLowerCase().includes('password') ? 'password' : 'text'}
                    placeholder="Value"
                    sx={{ flex: 1 }}
                  />
                  <Button
                    onClick={() => {
                      const newFields = credentialFields.filter((_, i) => i !== index);
                      setCredentialFields(newFields);
                    }}
                    disabled={credentialFields.length <= 1}
                    color="error"
                    sx={{ minWidth: 'auto' }}
                  >
                    Remove
                  </Button>
                </Box>
              ))}

              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCredentialFields([...credentialFields, { key: "", value: "" }]);
                }}
                sx={{ mt: 1 }}
              >
                Add Field
              </Button>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Security Note:</strong> Your credentials are filtered from AI input using placeholders.
                  Vision is automatically disabled when credentials are provided to prevent screenshot leaks.
                </Typography>
              </Alert>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Login Instructions (Optional)"
                value={loginInstructions}
                onChange={(e) => setLoginInstructions(e.target.value)}
                helperText="Provide step-by-step instructions for logging in. These will be automatically used if the session expires."
                sx={{ mt: 2 }}
                placeholder="Example: Click the 'Sign In' button, enter the username in the email field, enter the password, and click 'Log In'"
              />
            </Box>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={createBrowserSession}
            disabled={!url || (useResidentialProxy && !zipCode)}
            sx={{ mb: 2 }}
          >
            Start Live Browser Session
          </Button>
        </>
      )}

      {sessionStatus === 'creating' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Creating browser session...
          </Typography>
        </Box>
      )}

      {sessionStatus === 'ready' && liveUrl && (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Browser Session Ready!
            </Typography>
            <Typography variant="body2">
              Use the browser below to log in to the website. Once you're logged in, click "Save Session" below.
            </Typography>
          </Alert>

          {/* Iframe to show live browser session */}
          <Box
            sx={{
              mb: 3,
              border: '2px solid #ddd',
              borderRadius: 1,
              overflow: 'hidden',
              height: '500px',
            }}
          >
            <iframe
              src={/^https?:\/\/[a-zA-Z0-9]/.test(liveUrl) ? liveUrl : ""}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              allow="clipboard-read; clipboard-write"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="Browser Session"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={saveBrowserSession}
            >
              Save Session
            </Button>

            <Button
              variant="outlined"
              onClick={cancelSession}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {sessionStatus === 'success' && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="success">
            <Typography variant="body2">
              Browser session saved successfully! Your integration is ready.
            </Typography>
          </Alert>
        </Box>
      )}

      {sessionStatus === 'error' && errorMessage && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {errorMessage}
            </Typography>
          </Alert>

          <Button
            variant="outlined"
            fullWidth
            onClick={cancelSession}
          >
            Try Again
          </Button>
        </Box>
      )}
    </Box>
  );
}
